import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertCircle,
  ArrowLeft,
  Camera,
  Eye,
  Inbox,
  RefreshCw,
  StickyNote,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import {
  listWalkthroughs,
  listAreas,
  listPhotosForAreas,
  listNotesForAreas,
  signedUrlsForPaths,
} from '@/lib/pqvData';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch (_) {
    return String(iso);
  }
};

/**
 * Vision section — sits inside the PQ Intelligence page as a tab.
 *
 * Two views:
 *   - 'list': all walkthroughs for the current contractor (RLS-scoped).
 *   - 'viewer': read-only view of one walkthrough's areas, photos, and notes.
 *
 * Walkthroughs are part 1 of an estimate, so each row references an
 * estimate (estimate_id) — not a job. We display the estimate title and the
 * estimate's client so the user can identify the walkthrough at a glance.
 *
 * No edit affordances here — capture happens on PQVCapturePage.
 */
export const PQVisionSection = () => {
  const { t } = useTranslation();
  const [mode, setMode] = useState('list'); // 'list' | 'viewer'
  const [walkthroughs, setWalkthroughs] = useState([]);
  const [estimatesById, setEstimatesById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(null); // walkthrough row

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const ws = await listWalkthroughs();
      setWalkthroughs(ws);
      const estimateIds = Array.from(
        new Set(ws.map((w) => w.estimate_id).filter(Boolean))
      );
      if (estimateIds.length) {
        // Pull the estimate title + client_id, then resolve client names in a
        // single follow-up query so each row can show "Estimate · Client".
        const { data: estRows, error: eErr } = await supabase
          .from('estimates')
          .select('id,title,client_id')
          .in('id', estimateIds);
        if (eErr) throw eErr;
        const clientIds = Array.from(
          new Set((estRows || []).map((r) => r.client_id).filter(Boolean))
        );
        let clientMap = {};
        if (clientIds.length) {
          const { data: clientRows, error: cErr } = await supabase
            .from('clients')
            .select('id,name')
            .in('id', clientIds);
          if (cErr) throw cErr;
          for (const row of clientRows || []) clientMap[row.id] = row.name;
        }
        const map = {};
        for (const row of estRows || []) {
          map[row.id] = {
            id: row.id,
            title: row.title,
            client: row.client_id ? clientMap[row.client_id] || null : null,
          };
        }
        setEstimatesById(map);
      } else {
        setEstimatesById({});
      }
    } catch (e) {
      setError(e?.message || 'Failed to load walkthroughs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openWalkthrough = (w) => {
    setActive(w);
    setMode('viewer');
  };

  const backToList = () => {
    setActive(null);
    setMode('list');
  };

  if (loading) {
    return (
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-4 space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton
              key={i}
              className="h-10 w-full bg-[hsl(var(--muted))]"
            />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert
        className="border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
        data-testid="pqv-vision-error"
      >
        <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
        <AlertDescription className="text-sm">{error}</AlertDescription>
      </Alert>
    );
  }

  if (mode === 'viewer' && active) {
    return (
      <PQVisionWalkthroughViewer
        walkthrough={active}
        estimate={estimatesById[active.estimate_id] || null}
        onBack={backToList}
      />
    );
  }

  return (
    <div className="space-y-3" data-testid="pqv-vision-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[hsl(var(--primary))]">
          <Camera size={14} />
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            {t('pqv.vision_section_title', {
              defaultValue: 'Your walkthroughs',
            })}
          </div>
          <Badge
            variant="outline"
            className="ml-1 border-border text-muted-foreground text-[10px] uppercase tracking-[0.16em]"
          >
            {walkthroughs.length}
          </Badge>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
          data-testid="pqv-vision-refresh"
        >
          <RefreshCw size={12} className="mr-2" />
          {t('common.refresh', { defaultValue: 'Refresh' })}
        </Button>
      </div>

      {walkthroughs.length === 0 ? (
        <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
          <CardContent
            className="py-10 text-center"
            data-testid="pqv-vision-empty"
          >
            <div className="mx-auto size-10 rounded-md bg-[hsl(var(--muted))] border border-border flex items-center justify-center text-[hsl(var(--primary))]">
              <Inbox size={20} strokeWidth={1.75} />
            </div>
            <h3 className="mt-3 text-sm sm:text-base font-semibold text-foreground">
              {t('pqv.no_walkthroughs_title', {
                defaultValue: 'No walkthroughs yet',
              })}
            </h3>
            <p className="mt-1 text-xs text-muted-foreground max-w-md mx-auto">
              {t('pqv.no_walkthroughs_help', {
                defaultValue:
                  'Open an estimate and tap "Start walkthrough" to capture photos and notes per area.',
              })}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
          <CardContent className="p-2">
            <ul
              className="divide-y divide-border"
              data-testid="pqv-walkthrough-list"
            >
              {walkthroughs.map((w) => {
                const estimate = estimatesById[w.estimate_id];
                return (
                  <li
                    key={w.id}
                    className="flex items-center justify-between gap-3 px-3 py-3"
                    data-testid={`pqv-walkthrough-row-${w.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-foreground truncate">
                        {estimate?.title ||
                          t('pqv.unknown_estimate', {
                            defaultValue: 'Untitled estimate',
                          })}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {estimate?.client ? `${estimate.client} · ` : ''}
                        {formatDate(w.started_at)}
                      </div>
                    </div>
                    <Badge
                      variant="outline"
                      className="border-border text-muted-foreground text-[10px] uppercase tracking-[0.16em]"
                    >
                      {w.status || 'in_progress'}
                    </Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openWalkthrough(w)}
                      className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-[10px] h-7"
                      data-testid={`pqv-walkthrough-open-${w.id}`}
                    >
                      <Eye size={12} className="mr-1" />
                      {t('pqv.open_button', { defaultValue: 'Open' })}
                    </Button>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/* ----------------------------------------------------------------------------
 * Read-only walkthrough viewer — areas with their photos + notes inline.
 * Signed URLs are minted client-side per render so private storage objects
 * are loadable without exposing the bucket publicly.
 * ------------------------------------------------------------------------- */
export const PQVisionWalkthroughViewer = ({ walkthrough, estimate, onBack }) => {
  const { t } = useTranslation();
  const [areas, setAreas] = useState([]);
  const [photosByArea, setPhotosByArea] = useState({});
  const [notesByArea, setNotesByArea] = useState({});
  const [urlByPath, setUrlByPath] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const a = await listAreas(walkthrough.id);
        if (cancelled) return;
        setAreas(a);
        const ids = a.map((row) => row.id);
        const [photos, notes] = await Promise.all([
          listPhotosForAreas(ids),
          listNotesForAreas(ids),
        ]);
        if (cancelled) return;
        setPhotosByArea(photos);
        setNotesByArea(notes);
        // Sign URLs for every photo in one round trip.
        const allPaths = Object.values(photos)
          .flat()
          .map((p) => p.storage_path)
          .filter(Boolean);
        if (allPaths.length) {
          const map = await signedUrlsForPaths(allPaths);
          if (!cancelled) setUrlByPath(map);
        } else {
          setUrlByPath({});
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'Failed to load walkthrough.');
          toast.error(e?.message || 'Failed to load walkthrough.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [walkthrough.id]);

  const totalPhotos = useMemo(
    () => Object.values(photosByArea).reduce((acc, arr) => acc + arr.length, 0),
    [photosByArea]
  );

  return (
    <div className="space-y-3" data-testid="pqv-walkthrough-viewer">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            {t('pqv.viewer_eyebrow', { defaultValue: 'Walkthrough' })}
          </div>
          <div className="text-sm font-semibold text-foreground truncate">
            {estimate?.title ||
              t('pqv.unknown_estimate', {
                defaultValue: 'Untitled estimate',
              })}
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {estimate?.client ? `${estimate.client} · ` : ''}
            {formatDate(walkthrough.started_at)} ·{' '}
            {t('pqv.viewer_counts', {
              defaultValue: '{{areas}} areas · {{photos}} photos',
              areas: areas.length,
              photos: totalPhotos,
            })}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onBack}
          className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
          data-testid="pqv-viewer-back"
        >
          <ArrowLeft size={12} className="mr-2" />
          {t('pqv.back_to_list', { defaultValue: 'Back to list' })}
        </Button>
      </div>

      {error ? (
        <Alert
          className="border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
        >
          <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      ) : null}

      {loading ? (
        <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
          <CardContent className="p-4 space-y-3">
            <Skeleton className="h-32 w-full bg-[hsl(var(--muted))]" />
            <Skeleton className="h-32 w-full bg-[hsl(var(--muted))]" />
          </CardContent>
        </Card>
      ) : areas.length === 0 ? (
        <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {t('pqv.viewer_empty', {
              defaultValue: 'This walkthrough has no areas yet.',
            })}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {areas.map((area) => {
            const photos = photosByArea[area.id] || [];
            const notes = notesByArea[area.id] || [];
            return (
              <Card
                key={area.id}
                className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
                data-testid={`pqv-viewer-area-${area.id}`}
              >
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-sm font-semibold text-foreground">
                      {area.name}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className="border-border text-muted-foreground text-[10px] uppercase tracking-[0.16em]"
                      >
                        <Camera size={10} className="mr-1" />
                        {photos.length}
                      </Badge>
                      <Badge
                        variant="outline"
                        className="border-border text-muted-foreground text-[10px] uppercase tracking-[0.16em]"
                      >
                        <StickyNote size={10} className="mr-1" />
                        {notes.length}
                      </Badge>
                    </div>
                  </div>
                  {photos.length > 0 ? (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                      {photos.map((p) => {
                        const url = urlByPath[p.storage_path];
                        return (
                          <div
                            key={p.id}
                            className="aspect-square rounded-md overflow-hidden border border-border bg-[hsl(var(--muted))]"
                          >
                            {url ? (
                              <img
                                src={url}
                                alt=""
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                <Camera size={14} />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                  {notes.length > 0 ? (
                    <ul className="space-y-2">
                      {notes.map((n) => (
                        <li
                          key={n.id}
                          className="rounded-md border border-border bg-[hsl(var(--muted))]/30 px-3 py-2 text-sm text-foreground"
                        >
                          {n.body}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {photos.length === 0 && notes.length === 0 ? (
                    <div className="text-xs text-muted-foreground italic">
                      {t('pqv.area_empty', {
                        defaultValue: 'No photos or notes for this area.',
                      })}
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PQVisionSection;
